/** 指定時間だけ送信を待機する。 */
const sleep = ({ ms }: { ms: number }): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * HTTP 送信開始を直列化する。応答や再試行の待機中は後続の送信を妨げない。
 * @param params 最小送信間隔と時計・待機処理。
 * @returns 任意の送信期限を受け取り、期限切れなら送信枠を消費せず拒否する待機関数。
 */
export const createRequestScheduler = ({
  intervalMs,
  now = Date.now,
  sleepMs = sleep,
  getNotBefore = () => 0,
}: {
  intervalMs: number
  now?: () => number
  sleepMs?: (params: { ms: number }) => Promise<void>
  getNotBefore?: () => number
}) => {
  let nextStart = 0
  let queue = Promise.resolve()
  return ({ deadline }: { deadline?: number } = {}): Promise<void> => {
    const slot = queue.then(async () => {
      if (
        deadline !== undefined &&
        Math.max(now(), nextStart, getNotBefore()) >= deadline
      ) {
        throw new Error('Timeout')
      }
      while (Math.max(nextStart, getNotBefore()) > now()) {
        const start = Math.max(nextStart, getNotBefore())
        if (deadline !== undefined && start >= deadline)
          throw new Error('Timeout')
        await sleepMs({ ms: start - now() })
      }
      if (deadline !== undefined && now() >= deadline) {
        throw new Error('Timeout')
      }
      nextStart = now() + intervalMs
    })
    queue = slot.catch(() => {})
    return slot
  }
}

/** 同じ接続先を使うチェーン間で送信枠を共有する。 */
const schedulers = new Map<string, ReturnType<typeof createRequestScheduler>>()

/**
 * 接続先単位で送信頻度を制限する。
 * @param params 接続先キー、最小送信間隔、送信期限の Unix 時刻（ミリ秒）。
 * @returns 送信可能になると完了する Promise。
 */
export const waitForRequest = ({
  key,
  intervalMs,
  deadline,
}: {
  key: string
  intervalMs: number
  deadline?: number
}): Promise<void> => {
  let scheduler = schedulers.get(key)
  if (scheduler === undefined) {
    scheduler = createRequestScheduler({ intervalMs })
    schedulers.set(key, scheduler)
  }
  return scheduler({ deadline })
}
